import type { Delta } from 'quill/core';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { matchPath, useLocation } from 'react-router';
import {
  useCurrentRightPanelName,
  useRightPanel,
  useRightPanelWidth,
} from '../app/layout/RightPanel';
import { isFeatureEnabled } from '../utils/features';
import isExtensionPath from '../utils/isExtensionPath';
import useShortcut from '../utils/useShortcut';
import AiCopilotChatPanel, { useConversationData } from './AiCopilotChatPanel';
import useAiCopilotEnabled from './useAiCopilotEnabled';

const ToggleAiCopilotChatPanelContext = createContext<(() => void) | null>(
  null,
);

export const useToggleAiCopilotChatPanel = () =>
  useContext(ToggleAiCopilotChatPanelContext);

const StartAiCopilotConversationContext = createContext<
  ((message: Delta) => void) | null
>(null);

export const useStartAiCopilotConversation = () =>
  useContext(StartAiCopilotConversationContext);

const DISALLOWED_PATHS = ['/admin/*', '/settings/*'];
const DISALLOWED_PATH_EXCEPTIONS = ['/admin/insights'];

interface AiCopilotProps {
  children: React.ReactNode;
}

const RIGHT_PANEL_NAME = 'aiCopilot';

const isAiCopilotAllowed =
  isFeatureEnabled('ai_ask') && !isExtensionPath(window.location.pathname);

const AiCopilotProvider = !isAiCopilotAllowed
  ? ({ children }: AiCopilotProps) => children
  : ({ children }: AiCopilotProps) => {
      const { openRightPanel, closeRightPanel } = useRightPanel();
      const currentRightPanelName = useCurrentRightPanelName();
      const isChatPanelOpen = currentRightPanelName === RIGHT_PANEL_NAME;

      const width = useRightPanelWidth();
      const lastWidthRef = useRef<number | undefined>(undefined);

      useLayoutEffect(() => {
        if (isChatPanelOpen) {
          lastWidthRef.current = width;
        }
      }, [width, isChatPanelOpen]);

      const location = useLocation();

      const isCopilotDisallowed = useMemo(
        () =>
          DISALLOWED_PATHS.some(
            (path) =>
              matchPath(path, location.pathname) &&
              !DISALLOWED_PATH_EXCEPTIONS.some((exception) =>
                matchPath(exception, location.pathname),
              ),
          ),
        [location.pathname],
      );

      const isAiCopilotEnabled = useAiCopilotEnabled();

      useEffect(() => {
        if (isCopilotDisallowed) {
          closeRightPanel();
        }
      }, [isCopilotDisallowed, closeRightPanel]);

      const toggleChatPanel = useCallback(() => {
        if (isCopilotDisallowed) {
          return;
        }

        if (!isChatPanelOpen) {
          openRightPanel(<AiCopilotChatPanel />, {
            isResizable: true,
            initialWidth: lastWidthRef.current,
            name: RIGHT_PANEL_NAME,
          });
        } else {
          closeRightPanel();
        }
      }, [
        isChatPanelOpen,
        openRightPanel,
        closeRightPanel,
        isCopilotDisallowed,
      ]);

      const { startNewConversation } = useConversationData();

      const startAiCopilotConversation = useCallback(
        (message: Delta) => {
          if (isCopilotDisallowed) {
            return;
          }

          startNewConversation(message);
          openRightPanel(<AiCopilotChatPanel />, {
            isResizable: true,
            initialWidth: lastWidthRef.current,
            name: RIGHT_PANEL_NAME,
          });
        },
        [isCopilotDisallowed, openRightPanel, startNewConversation],
      );

      useShortcut(
        'mod+option+b',
        (e) => {
          e.preventDefault();
          toggleChatPanel();
        },
        {
          skip: !isAiCopilotEnabled,
          global: true,
        },
      );

      return (
        <StartAiCopilotConversationContext.Provider
          value={isAiCopilotEnabled ? startAiCopilotConversation : null}
        >
          <ToggleAiCopilotChatPanelContext.Provider
            value={isAiCopilotEnabled ? toggleChatPanel : null}
          >
            {children}
          </ToggleAiCopilotChatPanelContext.Provider>
        </StartAiCopilotConversationContext.Provider>
      );
    };

export default AiCopilotProvider;
